import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8080/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to every request automatically
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-refresh token on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isLogin = String(originalRequest?.url || '').includes('/auth/login');
    if (error.response?.status === 401 && !originalRequest._retry && !isLogin) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh/`, { refresh: refreshToken });
          const newAccessToken = refreshResponse.data.access;
          localStorage.setItem('access_token', newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
  );

// Auth
export const login = (username: string, password: string) =>
  api.post('/auth/login/', { username, password });

export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = () => api.get('/users/');
export const createUser = (data: any) => api.post('/users/', data);

// Projects
export const getProjects = () => api.get('/projects/');
export const getProject = (id: string) => api.get(`/projects/${id}/`);
export const createProject = (data: any) => api.post('/projects/', data);

// Dashboard
export const getMyWorkDashboard = () => api.get('/dashboard/my-work/');
export const getProjectDashboard = (projectId: string) =>
  api.get(`/dashboard/projects/${projectId}/`);

// Workflow Objects
export const getWorkflowObjects = (projectId: string, params?: any) =>
  api.get(`/projects/${projectId}/workflow-objects/`, { params });
export const createWorkflowObject = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/workflow-objects/`, data);
export const submitWorkflowObject = (id: string, data?: any) =>
  api.post(`/workflow-objects/${id}/submit/`, data);
export const reviewWorkflowObject = (id: string, data: any) =>
  api.post(`/workflow-objects/${id}/review/`, data);
export const closeWorkflowObject = (id: string, data?: any) =>
  api.post(`/workflow-objects/${id}/close/`, data);

// Master Data
export const getRoles = () => api.get('/users/roles/');
export const getContractors = () => api.get('/contractors/');
export const getActivities = (projectId: string) =>
  api.get(`/master/projects/${projectId}/activities/`);
export const getIssueTypes = () => api.get('/master/issue-types/');
export const getSeverityList = () => api.get('/master/severity/');
export const getHierarchy = (projectId: string) =>
  api.get(`/projects/${projectId}/hierarchy/`);

// NCR
export const getNCRs = (projectId: string, params?: any) =>
  api.get(`/projects/${projectId}/ncrs/`, { params });
export const createNCR = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/ncrs/`, data);

// RFI
export const getRFIs = (projectId: string, params?: any) =>
  api.get(`/projects/${projectId}/rfis/`, { params });
export const createRFI = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/rfis/`, data);
export const respondRFI = (rfiId: string, data: any) =>
  api.post(`/rfis/${rfiId}/respond/`, data);

// MIR
export const getMIRs = (projectId: string, params?: any) =>
  api.get(`/projects/${projectId}/mirs/`, { params });
export const createMIR = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/mirs/`, data);
export const submitMIRForReview = (mirId: string, formData: FormData) =>
  api.post(`/mirs/${mirId}/submit-for-review/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const submitMIRResult = (mirId: string, formData: FormData) =>
  api.post(`/mirs/${mirId}/submit-result/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const getMIRMaterialTypes = () =>
  api.get('/mir-material-types/');
export const getMIRChecklist = (materialType: string) =>
  api.get('/mir-checklist/', { params: { material_type: materialType } });

// WIR
export const getWIRs = (projectId: string, params?: any) =>
  api.get(`/projects/${projectId}/wirs/`, { params });
export const getWIR = (wirId: string) =>
  api.get(`/wirs/${wirId}/`);
export const createWIR = (projectId: string, formData: FormData) =>
  api.post(`/projects/${projectId}/wirs/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  export const closePunchPoint = (punchPointId: string, formData: FormData) =>
    api.post(`/wirs/punch-points/${punchPointId}/close/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  export const escalatePunchPoint = (punchPointId: string, data: { escalated_to: string; escalation_comments: string }) =>
    api.post(`/wirs/punch-points/${punchPointId}/escalate/`, data);
  
  // Snag Register
  export const getSnags = (projectId: string) =>
    api.get(`/projects/${projectId}/snags/`);
  export const createSnag = (projectId: string, formData: FormData) =>
    api.post(`/projects/${projectId}/snags/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  export const assignPunchPoint = (punchPointId: string, data: { assigned_to: string }) =>
    api.post(`/wirs/punch-points/${punchPointId}/assign/`, data);
  export const rectifyPunchPoint = (punchPointId: string, data: { rectification_remarks: string }) =>
    api.post(`/wirs/punch-points/${punchPointId}/rectify/`, data);
  export const rejectPunchPointRectification = (punchPointId: string, data: { rejection_remarks: string }) =>
    api.post(`/wirs/punch-points/${punchPointId}/reject-rectification/`, data);

// DPR
export const getDPRs = (projectId: string) =>
  api.get(`/projects/${projectId}/dprs/`);
export const createDPR = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/dprs/`, data);

// MOM
export const getMOMs = (projectId: string) =>
  api.get(`/projects/${projectId}/moms/`);
export const createMOM = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/moms/`, data);
export const createMOMAction = (momId: string, data: any) =>
  api.post(`/moms/${momId}/actions/`, data);
export const getMOMActions = (momId: string) =>
  api.get(`/moms/${momId}/actions/`);
// Site Instructions
export const getSiteInstructions = (projectId: string, params?: any) =>
  api.get(`/projects/${projectId}/site-instructions/`, { params });
export const createSiteInstruction = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/site-instructions/`, data);

// Notifications
export const getNotifications = () => api.get('/notifications/');
export const markNotificationRead = (id: string) =>
  api.patch(`/notifications/${id}/read/`);
export const markAllNotificationsRead = () =>
  api.patch('/notifications/read-all/');

// Search
export const search = (projectId: string, query: string) =>
  api.get(`/projects/${projectId}/search/?q=${query}`);

// Audit trail
export const getAuditTrail = (projectId?: string) =>
  api.get('/audit-trail/', { params: projectId ? { project: projectId } : {} });

export const getRecordTimeline = (recordId: string) =>
  api.get(`/records/${recordId}/timeline/`);

// Programs
export const getPrograms = () => api.get('/programs/');
export const createProgram = (data: any) => api.post('/programs/', data);

// Hierarchy
export const createHierarchyNode = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/hierarchy/`, data);

// Master data
export const createActivity = (projectId: string, data: any) =>
  api.post(`/master/projects/${projectId}/activities/`, data);

// Roles
export const assignUserToProject = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/members/`, data);
export const getProjectMembers = (projectId: string) =>
  api.get(`/projects/${projectId}/members/`);

// Quality audit rating
export const getRatingAccess = () => api.get('/rating/access/');
export const getRatingTemplates = () => api.get('/rating/templates/');
export const createRatingTemplate = (data: any) => api.post('/rating/templates/', data);
export const checkRatingTemplate = (templateId: string) =>
  api.get(`/rating/templates/${templateId}/check/`);
export const newRatingTemplateVersion = (templateId: string) =>
  api.post(`/rating/templates/${templateId}/new-version/`);
export const activateRatingTemplate = (templateId: string) =>
  api.post(`/rating/templates/${templateId}/activate/`);
export const downloadRatingTemplate = (templateId: string) =>
  api.get(`/rating/templates/${templateId}/excel/`, { responseType: 'blob' });
export const uploadRatingTemplate = (templateId: string, file: File, save: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('save', String(save));
  return api.post(`/rating/templates/${templateId}/excel/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getAvailableRatingTemplates = () => api.get('/rating/templates/available/');
export const getRatingSetup = (projectId: string) => api.get(`/projects/${projectId}/rating-setup/`);
export const setRatingSetup = (projectId: string, templateId: string) =>
  api.post(`/projects/${projectId}/rating-setup/`, { template_id: templateId });
export const getAuditVisits = (projectId: string) => api.get(`/projects/${projectId}/audit-visits/`);
export const createAuditVisit = (projectId: string, data: any) =>
  api.post(`/projects/${projectId}/audit-visits/`, data);

export const getVisitChecklist = (visitId: string) => api.get(`/audit-visits/${visitId}/checklist/`);
export const putVisitAnswer = (visitId: string, itemId: string, data: any) =>
  api.put(`/audit-visits/${visitId}/answers/${itemId}/`, data);
export const putVisitNC = (visitId: string, itemId: string, data: any) =>
  api.put(`/audit-visits/${visitId}/answers/${itemId}/nc/`, data);
export const setVisitScope = (visitId: string, data: any) => api.post(`/audit-visits/${visitId}/scope/`, data);
export const clearVisitArea = (visitId: string, itemId: string) =>
  api.post(`/audit-visits/${visitId}/scope/clear/`, { item_id: itemId });

export type MeetingKind = 'opening' | 'closing';
export const getMeeting = (visitId: string, kind: MeetingKind) =>
  api.get(`/audit-visits/${visitId}/meetings/${kind}/`);
export const putMeeting = (visitId: string, kind: MeetingKind, data: any) =>
  api.put(`/audit-visits/${visitId}/meetings/${kind}/`, data);
export const uploadAttendanceSheet = (visitId: string, kind: MeetingKind, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/audit-visits/${visitId}/meetings/${kind}/sheet/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getVisitReadiness = (visitId: string) => api.get(`/audit-visits/${visitId}/readiness/`);
export const submitAuditVisit = (visitId: string) => api.post(`/audit-visits/${visitId}/submit/`);

export const startVisitReview = (visitId: string) => api.post(`/audit-visits/${visitId}/start-review/`);
export const returnAuditVisit = (visitId: string, remarks: string, meetingAmendment = false) =>
  api.post(`/audit-visits/${visitId}/return/`, { remarks, meeting_amendment: meetingAmendment });
export const completeAuditVisit = (visitId: string) => api.post(`/audit-visits/${visitId}/complete/`);
export const reviewChangeAnswer = (visitId: string, itemId: string, data: any) =>
  api.post(`/audit-visits/${visitId}/answers/${itemId}/review-change/`, data);
export const getVisitResult = (visitId: string) => api.get(`/audit-visits/${visitId}/result/`);

export const getUserAccess = () => api.get('/users/access/');
export const updateUser = (userId: string, data: any) => api.patch(`/users/${userId}/`, data);
export const setUserPassword = (userId: string, password: string) =>
  api.post(`/users/${userId}/set-password/`, { password });
export const deactivateUser = (userId: string) => api.patch(`/users/${userId}/deactivate/`);
export const activateUser = (userId: string) => api.patch(`/users/${userId}/activate/`);

export const flagVisitPoint = (visitId: string, itemId: string, note: string) =>
  api.put(`/audit-visits/${visitId}/answers/${itemId}/flag/`, { note });
export const removeVisitFlag = (visitId: string, itemId: string) =>
  api.delete(`/audit-visits/${visitId}/answers/${itemId}/flag/`);
export const addressVisitFlag = (visitId: string, itemId: string, reply: string) =>
  api.post(`/audit-visits/${visitId}/answers/${itemId}/flag/address/`, { reply });

export const uploadVisitEvidence = (visitId: string, itemId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/audit-visits/${visitId}/answers/${itemId}/evidence/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteVisitEvidence = (visitId: string, itemId: string, evidenceId: string) =>
  api.delete(`/audit-visits/${visitId}/answers/${itemId}/evidence/${evidenceId}/`);

export const undoAddressVisitFlag = (visitId: string, itemId: string) =>
  api.delete(`/audit-visits/${visitId}/answers/${itemId}/flag/address/`);
export const getPointHistory = (visitId: string, itemId: string) =>
  api.get(`/audit-visits/${visitId}/answers/${itemId}/history/`);

export default api;